import { useEffect, useMemo, useState } from "react";
import StaffingModal from "./StaffingModal";
import { DataTable, ColumnDef } from "../../common/DataTable";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { MessageSquare } from "lucide-react";
import { Posting } from "@/types/staffing/posting";
import { Process } from "@/types/staffing/process";
import { getPostings, getProcesses } from "@/services/staffing/Staffing";
import { useApiClient } from "@/context/ApiContext";
import { getStatusBadgeColor, openStaffingGroupByReferenceId } from "@/utils/staffingUtil";

interface StaffInProcessStaffingModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    staff: string;
    staffName: string | null;
}

interface StaffProcessRow extends Process {
    Posting?: Posting;
}

const DOMAIN = 'positive';

const getColumns = (staffLoginName: string): ColumnDef<StaffProcessRow>[] => [
    {
        key: 'Chat',
        header: '',
        render: (row) => (
            <div className="flex w-8 items-center justify-center">
                {row.Posting && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-primary"
                        aria-label="Open staffing chat"
                        title="Staffing Chat"
                        onClick={() => openStaffingGroupByReferenceId(`${row.Id}-${staffLoginName}-${row.Posting!.ClientId}`)}
                    >
                        <MessageSquare className="h-4 w-4" />
                    </Button>
                )}
            </div>
        )
    },
    {
        key: 'PostingId',
        header: 'Posting ID',
        sortable: true
    },
    {
        key: 'Client',
        header: 'Client',
        sortable: true,
        sortValue: (row) => row.Posting?.Client?.NameFL ?? '',
        render: (row) => row.Posting
            ? row.Posting.Client?.NameFL ?? row.Posting.ClientId
            : <Skeleton className="h-4 w-full" />
    },
    {
        key: 'ServiceType',
        header: 'Service Type',
        sortable: true,
        sortValue: (row) => row.Posting?.ServiceType ?? '',
        render: (row) => row.Posting
            ? row.Posting.ServiceType
            : <Skeleton className="h-4 w-full" />
    },
    {
        key: 'PostingStatus',
        header: 'Posting Status',
        sortable: true,
        sortValue: (row) => row.Posting?.Status ?? '',
        render: (row) => row.Posting
            ? <Badge className={getStatusBadgeColor(row.Posting.Status)}>{row.Posting.Status}</Badge>
            : <Skeleton className="h-4 w-full" />
    },
    {
        key: 'Status',
        header: 'Process Status',
        sortable: true
    },
    {
        key: 'Source',
        header: 'Source',
        sortable: true
    },
    {
        key: 'Distance',
        header: 'Distance (mi)',
        sortable: true
    },
];

const StaffInProcessStaffingModal = ({ open, onOpenChange, staff, staffName }: StaffInProcessStaffingModalProps) => {
    const apiClient = useApiClient();
    const [rows, setRows] = useState<StaffProcessRow[]>([]);
    const [loading, setLoading] = useState(false);
    const staffLoginName = staff.includes('\\') ? staff.split('\\')[1] : staff;
    const columns = useMemo(() => getColumns(staffLoginName), [staffLoginName]);

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        const staffWithDomain = staff.includes('\\') ? staff : `${DOMAIN}\\${staff}`;
        getProcesses({ filter: `Staff eq '${staffWithDomain}' and Status ne 'Closed'`, orderby: 'Id desc' }, apiClient)
            .then(res => {
                const processes: Process[] = res.data.value ?? [];
                setRows(processes);
                const postingIds = processes.map(p => p.PostingId).filter((id, i, arr) => arr.indexOf(id) === i);
                if (postingIds.length === 0) return;
                return getPostings({ filter: postingIds.map(id => `Id eq ${id}`).join(' or '), expand: ['client'] }, apiClient)
                    .then(pres => {
                        const postings: Posting[] = pres.data.value ?? [];
                        const byId = new Map(postings.map(p => [p.Id, p]));
                        setRows(processes.map(pr => ({ ...pr, Posting: byId.get(pr.PostingId) })));
                    });
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    }, [open, staff, apiClient]);

    return (
        <StaffingModal open={open} onOpenChange={onOpenChange} title={`Staffing in Process — ${staffName ?? staff}`} wide>
            <DataTable
                data={rows}
                columns={columns}
                loading={loading}
            />
        </StaffingModal>
    );
};

export default StaffInProcessStaffingModal;
